import { simulatorStateSchema } from '@/domain/schema';
import type { SimulatorState } from '@/domain/types';

/**
 * Scénario "démo" pré-rempli au premier chargement (cf. DECISIONS.md §1.3) :
 * onboarding plus parlant qu'un formulaire vide, et valeurs cohérentes avec
 * les règles de validation de SPEC.md (acquisitionShare somme à 100%, etc.).
 *
 * IDs fixes et lisibles (plutôt que crypto.randomUUID()) pour garder un état
 * par défaut déterministe — important pour les tests de round-trip (AC6).
 */
export const defaultSimulatorState: SimulatorState = {
  version: '1.0.0',
  currency: 'EUR',
  info: {
    title: 'Scénario de démonstration',
    description:
      'Exemple de SaaS B2B à trois paliers (Starter, Pro, Enterprise) illustrant la montée en charge de l’acquisition et le chemin vers la rentabilité.',
  },
  team: {
    headcount: 1,
    avgAnnualCostPerFTE: 40_000,
    chargesMultiplier: 1.42,
  },
  categories: [
    { id: 'cat-infra', name: 'Infrastructure', color: '#0072B2' },
    { id: 'cat-marketing', name: 'Marketing', color: '#E69F00' },
    { id: 'cat-outils', name: 'Outils', color: '#009E73' },
    { id: 'cat-legal', name: 'Légal', color: '#999999' },
  ],
  fixedCosts: [
    {
      id: 'fixed-materiel',
      label: 'Achat matériel',
      amount: 4_500,
      categoryId: 'cat-infra',
      occursAtMonth: 1,
    },
    {
      id: 'fixed-juridique',
      label: 'Frais de constitution juridique',
      amount: 1_200,
      categoryId: 'cat-legal',
      occursAtMonth: 1,
    },
  ],
  recurringCosts: [
    {
      id: 'recurring-hebergement',
      label: 'Hébergement cloud',
      monthlyAmount: 350,
      categoryId: 'cat-infra',
    },
    {
      id: 'recurring-outils-saas',
      label: 'Outils SaaS (CRM, support, analytics)',
      monthlyAmount: 200,
      categoryId: 'cat-outils',
    },
    {
      id: 'recurring-pub',
      label: 'Publicité en ligne',
      monthlyAmount: 800,
      categoryId: 'cat-marketing',
    },
  ],
  tiers: [
    {
      id: 'tier-starter',
      name: 'Starter',
      monthlyPrice: 29,
      monthlyChurnRate: 0.07,
      acquisitionShare: 0.5,
    },
    {
      id: 'tier-pro',
      name: 'Pro',
      monthlyPrice: 79,
      monthlyChurnRate: 0.04,
      acquisitionShare: 0.35,
    },
    {
      id: 'tier-enterprise',
      name: 'Enterprise',
      monthlyPrice: 199,
      monthlyChurnRate: 0.02,
      acquisitionShare: 0.15,
    },
  ],
  acquisition: {
    newClientsPerMonth: 10,
    model: 's-curve',
    rampUpMonths: 6,
  },
  projection: {
    horizonMonths: 24,
  },
};

/**
 * État vierge : tout est vidé (aucune catégorie, aucun coût, effectif et acquisition à
 * zéro), par opposition à `defaultSimulatorState` qui pré-remplit un scénario de démo.
 * Sert de point de départ pour repartir d'une feuille blanche.
 *
 * Le schéma impose au moins un palier (`tiers.min(1)`) et une somme des parts
 * d'acquisition égale à 100 % : le strict minimum est donc un palier unique neutre,
 * à 0 € et 100 % des parts — et non une liste vide.
 */
export const emptySimulatorState: SimulatorState = {
  version: '1.0.0',
  currency: 'EUR',
  info: {
    title: 'Nouvelle simulation',
    description: '',
  },
  team: {
    headcount: 0,
    avgAnnualCostPerFTE: 0,
    chargesMultiplier: 1.42,
  },
  categories: [],
  fixedCosts: [],
  recurringCosts: [],
  tiers: [
    {
      id: 'tier-1',
      name: 'Palier 1',
      monthlyPrice: 0,
      monthlyChurnRate: 0,
      acquisitionShare: 1,
    },
  ],
  acquisition: {
    newClientsPerMonth: 0,
  },
  projection: {
    horizonMonths: 24,
  },
};

// Garde-fou : ces états de référence doivent toujours être des SimulatorState valides.
simulatorStateSchema.parse(defaultSimulatorState);
simulatorStateSchema.parse(emptySimulatorState);
